import * as React from 'react';
import { FieldRenderProps, Field, useForm } from 'react-final-form';
import { HelperText, HelperType } from '../HelperText';
import * as Classes from '../../common/classes';
import { RffFieldProps } from '../FieldProps';
import classNames from 'classnames';
import { ClassNameProp } from '../types';
import { Tooltip } from '../../tooltip/Tooltip';
import { FormGroup } from '../../form-group/FormGroup';
import { FieldValidator } from 'final-form';
import { useMemo } from 'react';

export enum ColSpanEnum {
  Two = 2,
  Three = 3,
  Four = 4,
}
export const FormItem: React.FC<
  React.PropsWithChildren<
    RffFieldProps &
      ClassNameProp & {
        label: React.ReactNode;
        disabled?: boolean;
        readOnly?: boolean;
        required?: boolean;
        inlineValidation?: boolean;
        contentClassName?: string;
        /**
         * Pokud je true, tooltip pro inline validaci bude max. široký jako obsah inputu.
         */
        inlineValidationFitContent?: boolean;
        hideValidationText?: boolean;
        hideValidationTextAndFlags?: boolean;
        validateWarning?: FieldValidator<any>;
        //omezení max velikost pro input s datum a časem
        colSpan?: ColSpanEnum;
      }
  >
> = ({
  name,
  hideValidationText,
  inlineValidation,
  inlineValidationFitContent,
  hideValidationTextAndFlags,
  children,
  label,
  className,
  contentClassName,
  validateWarning,
  required,
  disabled,
  readOnly,
  colSpan,
  formatOnBlur,
}) => {
  return (
    <Field name={name} formatOnBlur={formatOnBlur}>
      {({ meta, input }: FieldRenderProps<any>) => {
        return (
          <FormField
            label={label}
            validateWarning={validateWarning}
            input={input}
            meta={meta}
            disabled={disabled}
            readOnly={readOnly}
            required={required}
            colSpan={colSpan}
            className={className}
            contentClassName={contentClassName}
            inlineValidation={inlineValidation}
            inlineValidationFitContent={inlineValidationFitContent}
            hideValidationText={hideValidationText}
            hideValidationTextAndFlags={hideValidationTextAndFlags}
          >
            {children}
          </FormField>
        );
      }}
    </Field>
  );
};

type Props = {
  hideValidationText?: boolean;
  hideValidationTextAndFlags?: boolean;
  label: React.ReactNode;
  className?: string;
  contentClassName?: string;
  inlineValidation?: boolean;
  /**
   * Pokud je true, tooltip pro inline validaci bude max. široký jako obsah inputu.
   */
  inlineValidationFitContent?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  //omezení max velikost pro input s datum a časem
  colSpan?: ColSpanEnum;
  validateWarning?: FieldValidator<any>;
} & FieldRenderProps<any>;

const FORM_EMPTY_VALUES = {};

export const FormField: React.FC<Props> = ({
  meta,
  input: { value },
  label,
  validateWarning,
  children,
  colSpan,
  disabled,
  readOnly,
  required,
  className,
  contentClassName,
  inlineValidationFitContent,
  inlineValidation,
  hideValidationText,
  hideValidationTextAndFlags,
}) => {
  const form = useForm();
  const allValues = validateWarning ? form.getState().values : undefined;

  const warningText = useMemo(() => {
    if (validateWarning) {
      // @ts-ignore
      return validateWarning(value, allValues || FORM_EMPTY_VALUES, undefined);
    } else {
      return undefined;
    }
  }, [value, allValues, validateWarning]);

  let helperText = null;
  if (!hideValidationText && !hideValidationTextAndFlags) {
    if (warningText) {
      helperText = (
        <HelperText type={HelperType.WARNING}>{warningText}</HelperText>
      );
    }

    if (meta.touched) {
      if (meta.error || (meta.submitError && !meta.dirtySinceLastSubmit)) {
        helperText = (
          <HelperText type={HelperType.ERROR}>
            {meta.error || meta.submitError}
          </HelperText>
        );
      } else if (warningText) {
        helperText = (
          <HelperText type={HelperType.WARNING}>{warningText}</HelperText>
        );
      } else if (
        meta.pristine &&
        (meta.error ||
          (meta.submitError && !meta.dirtySinceLastSubmit) ||
          warningText)
      ) {
        helperText = (
          <HelperText type={HelperType.WARNING}>
            {meta.error || meta.submitError}
          </HelperText>
        );
      }
    }
  }

  const hasError =
    !hideValidationTextAndFlags &&
    meta.touched &&
    (meta.error || meta.submitError);
  const hasWarning = !hideValidationTextAndFlags && warningText;

  const cls = classNames(
    Classes.FORM_GROUP,
    Classes.FORM_GROUP_FORM,
    Classes.RF_FORM_GROUP,
    className,
    {
      [Classes.FORM_GROUP_HAS_HELPER_TEXT]: !!helperText,
      [Classes.RF_NO_ERROR_SPACE]: inlineValidation,
      [Classes.RF_ERROR]: hasError,
      [Classes.RF_WARNING]: !hasError && hasWarning,
      [Classes.RF_ACTIVE]: !!value,
      [Classes.RF_READONLY]: readOnly,
      [Classes.RF_DISABLED]: disabled,
      [Classes.RF_REQUIRED]: required,
      [Classes.FIELD_SIZE_2]: colSpan === ColSpanEnum.Two,
      [Classes.FIELD_SIZE_3]: colSpan === ColSpanEnum.Three,
      [Classes.FIELD_SIZE_4]: colSpan === ColSpanEnum.Four,
    }
  );

  return (
    <FormGroup
      className={cls}
      label={label}
      required={required}
      contentClassName={contentClassName}
    >
      {(!helperText || !inlineValidation) && (
        <>
          {children}
          {helperText}
        </>
      )}
      {inlineValidation && helperText && (
        <Tooltip
          intent={hasError ? 'danger' : hasWarning ? 'warning' : 'none'}
          trigger={
            <div
              className={Classes.RF_POPUP_TRIGGER}
              style={{
                width: inlineValidationFitContent ? 'fit-content' : 'auto',
              }}
            >
              {children}
            </div>
          }
        >
          {helperText}
        </Tooltip>
      )}
    </FormGroup>
  );
};

FormItem.displayName = 'VerticalFormItem';
